/**
 * How It Works — 4 variants (horizontal_3, vertical_timeline, screenshot_steps, image_cards)
 */
import type { HowItWorksContent } from '../schemas/how_it_works';
import {
  Body,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface HW {
  content: HowItWorksContent;
}

function StepNumber({ n }: { n: number }) {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'var(--ls-accent-soft)',
        color: 'var(--ls-accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.95rem',
        flexShrink: 0,
      }}
    >
      {n}
    </div>
  );
}

export function HowItWorksHorizontal3({ content }: HW) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.steps.slice(0, 4).map((s, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <StepNumber n={i + 1} />
              {/* 各ステップに画像が設定されている場合のみ表示。未設定のステップに空枠を出さない */}
              {s.image && (
                <VisualPlaceholder
                  src={s.image}
                  aspectRatio="16 / 10"
                  label={s.title}
                />
              )}
              <H3 style={{ fontSize: '1.125rem' }}>{s.title}</H3>
              <Body style={{ fontSize: '0.95rem' }}>{s.description}</Body>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function HowItWorksVerticalTimeline({ content }: HW) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            position: 'relative',
            maxWidth: 720,
          }}
        >
          {content.steps.map((s, i) => (
            <li key={i} style={{ display: 'flex', gap: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <StepNumber n={i + 1} />
                {i < content.steps.length - 1 && (
                  <div style={{ width: 2, flex: 1, background: 'var(--ls-border)' }} />
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 24 }}>
                <H3 style={{ fontSize: '1.125rem' }}>{s.title}</H3>
                <Body style={{ fontSize: '0.95rem' }}>{s.description}</Body>
                {/* 各ステップに画像が設定されている場合のみ表示。未設定のステップに空枠を出さない */}
                {s.image && (
                  <VisualPlaceholder
                    src={s.image}
                    aspectRatio="16 / 10"
                    label={s.title}
                  />
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </SectionFrame>
  );
}

export function HowItWorksScreenshotSteps({ content }: HW) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        {content.steps.map((s, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={i} className="ls-split-pair">
              <div className="ls-split-media" style={{ order: reverse ? 2 : 1 }}>
                <VisualPlaceholder src={s.image} aspectRatio="4 / 3" label={s.title} />
              </div>
              <div style={{ order: reverse ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <StepNumber n={i + 1} />
                <H3>{s.title}</H3>
                <Body>{s.description}</Body>
              </div>
            </div>
          );
        })}
      </div>
    </SectionFrame>
  );
}

export function HowItWorksImageCards({ content }: HW) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-3">
          {content.steps.slice(0, 4).map((s, i) => (
            <div key={i} className="ls-card" style={{ padding: 0, overflow: 'hidden' }}>
              <VisualPlaceholder src={s.image} aspectRatio="16 / 10" label={s.title} />
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <StepNumber n={i + 1} />
                <H3 style={{ fontSize: '1.125rem' }}>{s.title}</H3>
                <Body style={{ fontSize: '0.9rem' }}>{s.description}</Body>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const howItWorksVariants = {
  horizontal_3: HowItWorksHorizontal3,
  vertical_timeline: HowItWorksVerticalTimeline,
  screenshot_steps: HowItWorksScreenshotSteps,
  image_cards: HowItWorksImageCards,
};
